
import {Dispatch, SetStateAction, useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {ErrorHandler} from "../utils";
import {AlertDescriptor} from "./AlertDescriptor";
import {AlertableError} from "./AlertableError";

export interface AlertController {
  alert: AlertDescriptor | undefined;
  setAlert: Dispatch<SetStateAction<AlertDescriptor | undefined>>;
  clearAlert: () => void;
  createErrorAlert: ErrorHandler;
}

export interface UseAlertOptions {
  errorTitleKey?: string;
}

export function useAlert({
  errorTitleKey
}: UseAlertOptions = {}): AlertController {
  const {t} = useTranslation();
  const [alert, setAlert] = useState<AlertDescriptor>();

  const resolveTitle = useCallback(
    (error: any): string | undefined => {
      if (error instanceof AlertableError && error.title !== undefined) {
        return error.title === null ? undefined : error.title;
      }
      if (errorTitleKey) {
        return t(errorTitleKey) || undefined;
      }
      return undefined;
    },
    [errorTitleKey, t]
  );

  return useMemo<AlertController>(
    () => ({
      alert,
      setAlert,
      clearAlert: () => setAlert(undefined),
      createErrorAlert: (error: any) => {
        console.error(error);
        setAlert({
          type: error instanceof AlertableError ? error.severity : "error",
          title: resolveTitle(error),
          content: () => (
            <span className={"alert-message"}>{error.message}</span>
          )
        });
      }
    }),
    [alert, resolveTitle]
  );
}
